// Tipos e interfaces para HL7 FHIR R4 AllergyIntolerance
// Baseado na especificação oficial: https://hl7.org/fhir/R4/allergyintolerance.html

// Enums para AllergyIntolerance
export enum FHIRAllergyIntoleranceClinicalStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  RESOLVED = 'resolved'
}

export enum FHIRAllergyIntoleranceVerificationStatus {
  UNCONFIRMED = 'unconfirmed',
  CONFIRMED = 'confirmed', 
  REFUTED = 'refuted',
  ENTERED_IN_ERROR = 'entered-in-error'
}

export enum FHIRAllergyIntoleranceType {
  ALLERGY = 'allergy',
  INTOLERANCE = 'intolerance'
}

export enum FHIRAllergyIntoleranceCategory {
  FOOD = 'food',
  MEDICATION = 'medication',
  ENVIRONMENT = 'environment',
  BIOLOGIC = 'biologic'
}

export enum FHIRAllergyIntoleranceCriticality {
  LOW = 'low',
  HIGH = 'high',
  UNABLE_TO_ASSESS = 'unable-to-assess'
}

export enum FHIRAllergyIntoleranceSeverity {
  MILD = 'mild',
  MODERATE = 'moderate',
  SEVERE = 'severe'
}

// Interfaces para estruturas complexas
export interface FHIRCodeableConcept {
  coding?: FHIRCoding[]
  text?: string
}

export interface FHIRCoding {
  system?: string
  version?: string
  code?: string
  display?: string
  userSelected?: boolean
}

export interface FHIRIdentifier {
  use?: string
  type?: FHIRCodeableConcept
  system?: string
  value?: string
  period?: FHIRPeriod
  assigner?: FHIRReference
}

export interface FHIRReference {
  reference?: string
  type?: string
  identifier?: FHIRIdentifier
  display?: string
}

export interface FHIRPeriod {
  start?: string // dateTime
  end?: string // dateTime
}

export interface FHIRAge {
  value?: number
  comparator?: string
  unit?: string
  system?: string
  code?: string
}

export interface FHIRRange {
  low?: FHIRQuantity
  high?: FHIRQuantity
}

export interface FHIRQuantity {
  value?: number
  comparator?: string
  unit?: string
  system?: string
  code?: string
}

export interface FHIRAnnotation {
  authorReference?: FHIRReference
  authorString?: string
  time?: string // dateTime
  text: string
}

// Interface para reações alérgicas
export interface FHIRAllergyIntoleranceReaction {
  substance?: FHIRCodeableConcept
  manifestation: FHIRCodeableConcept[]
  description?: string
  onset?: string // dateTime
  severity?: FHIRAllergyIntoleranceSeverity
  exposureRoute?: FHIRCodeableConcept
  note?: FHIRAnnotation[]
}

// Interface principal para AllergyIntolerance
export interface FHIRAllergyIntolerance {
  resourceType: 'AllergyIntolerance'
  id?: string
  meta?: {
    versionId?: string
    lastUpdated?: string // instant
    source?: string
    profile?: string[]
    security?: FHIRCoding[]
    tag?: FHIRCoding[]
  }
  implicitRules?: string // uri
  language?: string // code
  text?: {
    status: 'generated' | 'extensions' | 'additional' | 'empty'
    div: string // xhtml
  }
  contained?: any[] // Resource[]
  extension?: any[] // Extension[]
  modifierExtension?: any[] // Extension[]
  
  // Campos específicos do AllergyIntolerance
  identifier?: FHIRIdentifier[]
  clinicalStatus?: FHIRCodeableConcept
  verificationStatus?: FHIRCodeableConcept
  type?: FHIRAllergyIntoleranceType
  category?: FHIRAllergyIntoleranceCategory[]
  criticality?: FHIRAllergyIntoleranceCriticality
  code?: FHIRCodeableConcept // Substância/agente causador
  patient: FHIRReference // Obrigatório
  encounter?: FHIRReference
  onsetDateTime?: string
  onsetAge?: FHIRAge
  onsetPeriod?: FHIRPeriod
  onsetRange?: FHIRRange
  onsetString?: string
  recordedDate?: string // dateTime
  recorder?: FHIRReference
  asserter?: FHIRReference
  lastOccurrence?: string // dateTime
  note?: FHIRAnnotation[]
  reaction?: FHIRAllergyIntoleranceReaction[]
}

// Interface para mapeamento com sistema interno
export interface AllergyIntoleranceMapping {
  fhir: FHIRAllergyIntolerance
  internal: any
}

// Utilitários para validação
export const FHIR_ALLERGY_INTOLERANCE_REQUIRED_FIELDS = [
  'resourceType',
  'patient'
] as const

export const FHIR_ALLERGY_INTOLERANCE_OPTIONAL_FIELDS = [
  'id',
  'identifier',
  'clinicalStatus',
  'verificationStatus',
  'type',
  'category',
  'criticality',
  'code',
  'onsetDateTime',
  'recordedDate',
  'reaction'
] as const

// Códigos SNOMED CT comuns para alergias
export const COMMON_ALLERGY_CODES = {
  // Alimentos
  PEANUT: { code: '256349002', display: 'Peanut' },
  MILK: { code: '3718001', display: 'Cow\'s milk' },
  EGG: { code: '102263004', display: 'Eggs' },
  SHELLFISH: { code: '44027008', display: 'Seafood' },
  
  // Medicamentos
  PENICILLIN: { code: '764146007', display: 'Penicillin' },
  ASPIRIN: { code: '387458008', display: 'Aspirin' },
  IBUPROFEN: { code: '387207008', display: 'Ibuprofen' },
  
  // Ambientais
  POLLEN: { code: '256277009', display: 'Grass pollen' },
  DUST_MITE: { code: '260147004', display: 'House dust mite' },
  LATEX: { code: '111088007', display: 'Latex' }
} as const

// Manifestações comuns de reações alérgicas
export const COMMON_MANIFESTATIONS = {
  RASH: { code: '271807003', display: 'Rash' },
  HIVES: { code: '126485001', display: 'Urticaria' },
  ITCHING: { code: '418363000', display: 'Itching' },
  SWELLING: { code: '423666004', display: 'Swelling' },
  DIFFICULTY_BREATHING: { code: '267036007', display: 'Dyspnea' },
  ANAPHYLAXIS: { code: '39579001', display: 'Anaphylaxis' },
  NAUSEA: { code: '422587007', display: 'Nausea' },
  VOMITING: { code: '422400008', display: 'Vomiting' },
  DIARRHEA: { code: '62315008', display: 'Diarrhea' }
} as const